import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { UserAvatar } from "@/components/ui/UserAvatar";

interface Props {
  selected: string[];
  onChange: (ids: string[]) => void;
  userSearch: string;
  onUserSearchChange: (value: string) => void;
}

export function UserMultiSelect({ selected, onChange, userSearch, onUserSearchChange }: Props) {
  const { data: rawUsers = [] } = useInternalUsers({ includeFridayContractors: true });
  const users = [...rawUsers].sort((a, b) => a.full_name.localeCompare(b.full_name, "pl"));

  const visibleUsers = userSearch.trim()
    ? users.filter((u) => u.full_name.toLowerCase().includes(userSearch.toLowerCase()))
    : users;

  function toggle(id: string) {
    onChange(
      selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]
    );
  }

  function selectAll() {
    onChange([]);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <span className="text-sm text-gray-500 dark:text-gray-400 mr-1">Osoby:</span>

        {/* "Wszyscy" chip */}
        <button
          onClick={selectAll}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
            selected.length === 0
              ? "bg-gray-800 text-white border-gray-800"
              : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 hover:border-gray-500"
          }`}
        >
          Wszyscy
        </button>

        <input
          type="text"
          value={userSearch}
          onChange={(e) => onUserSearchChange(e.target.value)}
          placeholder="Szukaj użytkownika..."
          className="text-xs px-2.5 py-1 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] w-44"
        />
      </div>

      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pr-1">
        {visibleUsers.map((u) => {
          const active = selected.includes(u.id);
          return (
            <button
              key={u.id}
              onClick={() => toggle(u.id)}
              title={u.full_name}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                active
                  ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                  : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 hover:border-[var(--color-primary)]"
              }`}
            >
              <UserAvatar
                userId={u.id}
                fullName={u.full_name}
                profilePictureUrl={u.profile_picture_url}
                size="xs"
                initialsClassName={active ? "bg-white dark:bg-gray-800/20 text-white font-bold" : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-bold"}
              />
              {u.full_name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
